/**
 * DuerpPage - Vue synthèse d'un DUERP
 * Affiche les infos établissement + liste des unités de travail
 */

import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { Card, CardBody, CardHeader } from '../components/ui/Card'
import Breadcrumb from '../components/ui/Breadcrumb'
import EmptyState from '../components/ui/EmptyState'
import {
  duerpAPI,
  unitesAPI,
  DuerpDocumentFull,
  DuerpUnite,
  SECTEUR_OPTIONS,
} from '../services/duerp'
import { useAuthStore } from '../stores/authStore'

export default function DuerpPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { isAuthenticated, openLoginModal } = useAuthStore()

  const [document, setDocument] = useState<DuerpDocumentFull | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAddUnite, setShowAddUnite] = useState(false)
  const [editingUnite, setEditingUnite] = useState<DuerpUnite | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  // Charger le document
  useEffect(() => {
    if (!isAuthenticated || !id) {
      setIsLoading(false)
      return
    }

    const loadDocument = async () => {
      try {
        setIsLoading(true)
        const result = await duerpAPI.get(id)
        setDocument(result.document)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de chargement')
      } finally {
        setIsLoading(false)
      }
    }

    loadDocument()
  }, [isAuthenticated, id])

  // Recharger après modification
  const reload = async () => {
    if (!id) return
    try {
      const result = await duerpAPI.get(id)
      setDocument(result.document)
    } catch (err) {
      console.error('Erreur rechargement:', err)
    }
  }

  // Ajouter une unité
  const handleAddUnite = async (data: { nom: string; description?: string; effectif?: number }) => {
    if (!id) return
    try {
      await unitesAPI.create(id, data)
      await reload()
      setShowAddUnite(false)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur')
    }
  }

  // Modifier une unité
  const handleUpdateUnite = async (
    uniteId: string,
    data: { nom?: string; description?: string; effectif?: number }
  ) => {
    if (!id) return
    try {
      await unitesAPI.update(id, uniteId, data)
      await reload()
      setEditingUnite(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur')
    }
  }

  // Supprimer une unité
  const handleDeleteUnite = async (uniteId: string) => {
    if (!id) return
    try {
      await unitesAPI.delete(id, uniteId)
      await reload()
      setDeleteConfirm(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur')
    }
  }

  // Supprimer le DUERP
  const handleDeleteDuerp = async () => {
    if (!id || !window.confirm('Supprimer ce DUERP et toutes ses données ?')) return
    try {
      await duerpAPI.delete(id)
      navigate('/duerp')
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur')
    }
  }

  // Export PDF
  const handleExport = async () => {
    if (!id) return
    try {
      const result = await duerpAPI.getExport(id)
      // Stocker les données d'export et naviguer vers la page d'export
      sessionStorage.setItem('duerp_export', JSON.stringify(result.export))
      navigate(`/duerp/${id}/export`)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur lors de la préparation de l\'export')
    }
  }

  // Non authentifié
  if (!isAuthenticated) {
    return (
      <div className="max-w-2xl mx-auto py-12 px-4 text-center">
        <Card>
          <CardBody className="py-12">
            <p className="text-gray-600 mb-6">Connexion requise</p>
            <button onClick={openLoginModal} className="btn-primary">
              Se connecter
            </button>
          </CardBody>
        </Card>
      </div>
    )
  }

  // Chargement
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    )
  }

  // Erreur
  if (error || !document) {
    return (
      <div className="max-w-xl mx-auto py-12 px-4">
        <Card className="border-red-200 bg-red-50">
          <CardBody className="text-center py-8">
            <p className="text-red-600">{error || 'Document non trouvé'}</p>
            <Link to="/duerp" className="mt-4 inline-block text-sm text-red-700 underline">
              Retour à la liste
            </Link>
          </CardBody>
        </Card>
      </div>
    )
  }

  // Comptages
  const totalSubstances = document.unites.reduce(
    (sum, u) => sum + (u.substances?.length || 0),
    0
  )
  const totalCMR = document.unites.reduce(
    (sum, u) =>
      sum +
      (u.substances?.filter((s) => s.donnees_reglementaires?.est_cmr).length || 0),
    0
  )

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Accueil', href: '/' },
          { label: 'Mes DUERP', href: '/duerp' },
          { label: document.raison_sociale },
        ]}
      />

      {/* Actions */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-xl font-bold text-gray-900 truncate">
          {document.raison_sociale}
        </h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExport}
            className="btn-secondary flex items-center gap-2"
            disabled={document.unites.length === 0 || totalSubstances === 0}
            title={
              document.unites.length === 0
                ? 'Ajoutez au moins une unité'
                : totalSubstances === 0
                  ? 'Ajoutez au moins une substance'
                  : 'Exporter en PDF'
            }
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Exporter PDF
          </button>
          <button
            onClick={handleDeleteDuerp}
            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
            title="Supprimer"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Infos établissement */}
      <Card>
        <CardHeader className="border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Établissement</h2>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Adresse :</span>
              <span className="ml-2 text-gray-900">
                {document.adresse}
                {document.code_postal && ` - ${document.code_postal}`}
                {document.ville && ` ${document.ville}`}
              </span>
            </div>
            {document.siret && (
              <div>
                <span className="text-gray-500">SIRET :</span>
                <span className="ml-2 text-gray-900">{document.siret}</span>
              </div>
            )}
            {document.effectif_total && (
              <div>
                <span className="text-gray-500">Effectif :</span>
                <span className="ml-2 text-gray-900">{document.effectif_total} personnes</span>
              </div>
            )}
            {document.secteur_activite && (
              <div>
                <span className="text-gray-500">Secteur :</span>
                <span className="ml-2 text-gray-900">
                  {SECTEUR_OPTIONS.find((s) => s.value === document.secteur_activite)?.label ||
                    document.secteur_activite}
                </span>
              </div>
            )}
            {document.responsable_nom && (
              <div>
                <span className="text-gray-500">Responsable :</span>
                <span className="ml-2 text-gray-900">
                  {document.responsable_nom}
                  {document.responsable_fonction && ` (${document.responsable_fonction})`}
                </span>
              </div>
            )}
            <div>
              <span className="text-gray-500">Identifiant :</span>
              <span className="ml-2 text-gray-900 font-mono text-xs">
                {document.document_id}
              </span>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Synthèse indicateurs */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardBody className="text-center py-4">
            <p className="text-2xl font-bold text-gray-900">{document.unites.length}</p>
            <p className="text-sm text-gray-500">Unité(s) de travail</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center py-4">
            <p className="text-2xl font-bold text-gray-900">{totalSubstances}</p>
            <p className="text-sm text-gray-500">Substance(s)</p>
          </CardBody>
        </Card>
        <Card className={totalCMR > 0 ? 'border-red-200 bg-red-50' : ''}>
          <CardBody className="text-center py-4">
            <p className={`text-2xl font-bold ${totalCMR > 0 ? 'text-red-600' : 'text-gray-900'}`}>
              {totalCMR}
            </p>
            <p className="text-sm text-gray-500">CMR identifié(s)</p>
          </CardBody>
        </Card>
      </div>

      {/* Alerte CMR */}
      {totalCMR > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="font-medium">Point de vigilance : {totalCMR} substance(s) CMR</p>
              <p className="mt-1 text-red-700">
                Suivi individuel renforcé (SIR) obligatoire. Se référer aux fiches substances pour les
                recommandations spécifiques.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Unités de travail */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Unités de travail</h2>
          <button
            onClick={() => setShowAddUnite(true)}
            className="btn-primary flex items-center gap-2 text-sm"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Ajouter une unité
          </button>
        </div>

        {/* Message pédagogique */}
        {document.unites.length > 0 && (
          <p className="text-sm text-gray-500 -mt-2">
            Cliquez sur une unité pour gérer les substances et décrire le contexte d'exposition.
          </p>
        )}

        {document.unites.length === 0 ? (
          <EmptyState
            icon={
              <svg
                className="w-full h-full"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                />
              </svg>
            }
            title="Aucune unité de travail"
            description="Ajoutez des unités pour structurer votre document et y associer des substances chimiques."
            action={{
              label: 'Ajouter une unité',
              onClick: () => setShowAddUnite(true),
            }}
          />
        ) : (
          <div className="grid gap-4">
            {document.unites.map((unite) => (
              <UniteCard
                key={unite.id}
                unite={unite}
                duerpId={document.id}
                onEdit={() => setEditingUnite(unite)}
                onDelete={() => setDeleteConfirm(unite.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Mention multi-substances */}
      {totalSubstances > 1 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-xs text-amber-800">
          Cette synthèse ne constitue pas une évaluation toxicologique des expositions
          combinées. Pour toute analyse approfondie des effets de co-expositions, consulter
          un spécialiste compétent (toxicologue, hygiéniste industriel).
        </div>
      )}

      {/* Modal ajout unité */}
      {showAddUnite && (
        <UniteModal
          onClose={() => setShowAddUnite(false)}
          onSave={handleAddUnite}
        />
      )}

      {/* Modal édition unité */}
      {editingUnite && (
        <UniteModal
          unite={editingUnite}
          onClose={() => setEditingUnite(null)}
          onSave={(data) => handleUpdateUnite(editingUnite.id, data)}
        />
      )}

      {/* Confirmation suppression */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Supprimer cette unité ?
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Toutes les substances associées seront également supprimées.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDeleteUnite(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Carte unité de travail
 * Cliquable pour accéder à la gestion des substances
 */
function UniteCard({
  unite,
  duerpId,
  onEdit,
  onDelete,
}: {
  unite: DuerpUnite
  duerpId: string
  onEdit: () => void
  onDelete: () => void
}) {
  const navigate = useNavigate()
  const nbSubstances = unite.substances?.length || 0
  const nbCMR =
    unite.substances?.filter((s) => s.donnees_reglementaires?.est_cmr).length || 0

  // Navigation vers la page de gestion des substances
  const handleCardClick = () => {
    navigate(`/duerp/${duerpId}/unite/${unite.id}`)
  }

  return (
    <Card
      className="hover:shadow-md hover:border-blue-300 transition-all cursor-pointer group"
      onClick={handleCardClick}
    >
      <CardBody>
        <div className="flex items-start justify-between gap-4">
          {/* Icône chimie */}
          <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
            <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          </div>

          <div className="min-w-0 flex-1">
            <div className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
              {unite.nom}
            </div>
            {unite.description && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                {unite.description}
              </p>
            )}
            <div className="flex items-center gap-4 mt-2">
              {unite.effectif && (
                <span className="text-xs text-gray-500">
                  {unite.effectif} personne(s)
                </span>
              )}
              <span className="text-xs text-gray-500">{nbSubstances} substance(s)</span>
              {nbCMR > 0 && (
                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                  {nbCMR} CMR
                </span>
              )}
            </div>
            {/* Indication action */}
            <p className="text-xs text-blue-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
              Gérer les substances et le contexte d'exposition
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => {
                e.stopPropagation()
                onEdit()
              }}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              title="Modifier l'unité"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
              title="Supprimer l'unité"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
            {/* Chevron indicateur */}
            <div className="p-2 text-gray-400 group-hover:text-blue-600 transition-colors">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

/**
 * Modal unité (création / édition)
 */
function UniteModal({
  unite,
  onClose,
  onSave,
}: {
  unite?: DuerpUnite
  onClose: () => void
  onSave: (data: { nom: string; description?: string; effectif?: number }) => Promise<void>
}) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    nom: unite?.nom || '',
    description: unite?.description || '',
    effectif: unite?.effectif?.toString() || '',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formData.nom.trim()) return

    try {
      setIsSubmitting(true)
      await onSave({
        nom: formData.nom.trim(),
        description: formData.description.trim() || undefined,
        effectif: formData.effectif ? parseInt(formData.effectif) : undefined,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {unite ? 'Modifier l\'unité' : 'Nouvelle unité de travail'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom de l'unité *
            </label>
            <input
              type="text"
              value={formData.nom}
              onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ex: Atelier peinture"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Activités principales..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Effectif
            </label>
            <input
              type="number"
              value={formData.effectif}
              onChange={(e) => setFormData({ ...formData, effectif: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Nombre de personnes"
              min="1"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button type="submit" className="btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Enregistrement...' : unite ? 'Enregistrer' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
